import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Checkbox,
    FormControlLabel,
    Chip,
    Radio,
    RadioGroup
} from '@mui/material';
import {
    Tag,
    Search,
    X as CloseIcon,
    Save,
    Code,
    Workflow,
    AlertCircle,
    Variable,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import api from './api';
import { toast } from 'react-toastify';
import VariableSelector from './VariableSelector';
import TriggerConditionsForm from './components/TriggerConditionsForm';

const TemplateManager = () => {
  // Basic state definitions
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentType, setContentType] = useState('workflow');
  const [localVarMappings, setLocalVarMappings] = useState([]);
  const [tags, setTags] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [codes, setCodes] = useState([]);
  const [variableSelectorOpen, setVariableSelectorOpen] = useState(false);
  const [selectedVarName, setSelectedVarName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTaskTags, setSelectedTaskTags] = useState([]);
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [selectedLocalVariable, setSelectedLocalVariable] = useState(null);
  const [variableMappings, setVariableMappings] = useState([]);

  // Filter states
  const [tagTemplateFilter, setTagTemplateFilter] = useState('all'); // 'all', 'hasTemplate', 'noTemplate'
  const [taskTypeFilter, setTaskTypeFilter] = useState('all'); // 'all', 'workflow', 'code'
  const [modalSearchTerm, setModalSearchTerm] = useState(''); // Search in modal

  // State for tag trigger conditions
  const [tagTriggerConditions, setTagTriggerConditions] = useState({
    triggerType: 'change',  // 'change', 'threshold', 'edge'
    operator: 'equals',     // 'equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'
    thresholdValue: '',
    risingEdge: true       // true for rising edge, false for falling edge
  });

  // Operator options
  const triggerOperators = [
    { value: 'equals', label: 'Equals (=)', icon: '=' },
    { value: 'notEquals', label: 'Not Equals (≠)', icon: '≠' },
    { value: 'greaterThan', label: 'Greater Than (>)', icon: '>' },
    { value: 'lessThan', label: 'Less Than (<)', icon: '<' },
    { value: 'greaterThanOrEqual', label: 'Greater Or Equal (≥)', icon: '≥' },
    { value: 'lessThanOrEqual', label: 'Less Or Equal (≤)', icon: '≤' }
  ];

  // Trigger types
  const triggerTypes = [
    { value: 'change', label: 'Any Change' },
    { value: 'threshold', label: 'Threshold Value' },
    { value: 'edge', label: 'Edge Triggering' }
  ];

  // Task bazlı trigger conditions state
  const [taskTriggerConditions, setTaskTriggerConditions] = useState({
    triggerType: 'change',  
    operator: 'equals',     
    thresholdValue: '',
    risingEdge: true
  });
  // useMemo for filtered tags
  const filteredTags = useMemo(() => {
    return tags.filter(tag => 
      tag.tagName && tag.tagName.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  }, [tags, tagSearchTerm]);

  // useMemo for filtered tasks
  const filteredTasks = useMemo(() => {
    const allTasks = [...workflows, ...codes];

    return allTasks.filter(task => {
      // Search filter
      const matchesSearch = !taskSearchTerm || (task.name && task.name.toLowerCase().includes(taskSearchTerm.toLowerCase()));

      // Type filter
      let matchesType = true;
      if (taskTypeFilter === 'workflow') {
        matchesType = task.contentType === 'workflow';
      } else if (taskTypeFilter === 'code') {
        matchesType = task.contentType === 'code';
      }

      return matchesSearch && matchesType;
    });
  }, [workflows, codes, taskSearchTerm, taskTypeFilter]);
  // Initial data loading useEffect
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Debug: Check SessionStorage
      const companyId = sessionStorage.getItem('companyId');
      console.log('TemplateManager - CompanyId from sessionStorage:', companyId);
      
      // If companyId doesn't exist, assign default value 1
      if (!companyId) {
        console.log('CompanyId not found in sessionStorage, setting default value 1');
        sessionStorage.setItem('companyId', '1');
        sessionStorage.setItem('userId', '1');
      }
      
      try {
        const [tagsData, workflowsData, codesData, templateStatusData] = await Promise.all([
          api.dataSources.getAllTags(),  // Tags tablosundan veri çek
          api.getWorkflows(),
          api.getCodes(),
          api.getAllTagTemplateStatus()
        ]);

        console.log('API Response - tagsData:', tagsData?.length, 'items', tagsData);
        console.log('API Response - workflowsData:', workflowsData?.length, 'items', workflowsData);
        console.log('API Response - codesData:', codesData?.length, 'items', codesData);
        console.log('API Response - templateStatusData:', templateStatusData?.length, 'items', templateStatusData);

        // Convert data from Tags table to proper format
        const formattedTags = tagsData.map(tag => ({
          tagID: tag.TagID || tag.tagId,
          tagName: tag.TagName || tag.tagName,
          name: tag.TagName || tag.tagName,
          type: 'T',  // All tags are type 'T'
          value: tag.Value || tag.currentValue || 0
        }));
        setTags(formattedTags);
        console.log('Formatted Tags:', formattedTags?.length, 'items');
        const workflowsWithTags = workflowsData.map(w => ({
          ...w,
          contentType: 'workflow',
          tagCount: templateStatusData.filter(ts => 
            ts.HasTemplate === 1 && 
            ts.ContentType === 'workflow' && 
            ts.TaskName === w.name
          ).length
        }));

        const codesWithTags = codesData.map(c => ({
          ...c,
          contentType: 'code',
          tagCount: templateStatusData.filter(ts => 
            ts.HasTemplate === 1 && 
            ts.ContentType === 'code' && 
            ts.TaskName === c.name
          ).length
        }));

        setWorkflows(workflowsWithTags);
        setCodes(codesWithTags);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Error occurred while loading data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Template data loading function
  const loadTemplateData = async (tagId) => {
    try {
      setLoading(true);
      setSelectedContent(null);
      setLocalVarMappings([]);
      setSelectedVariables([]);
      
      const templateData = await api.getTagTemplateMappings(tagId);
      console.log('Tag template data from API:', templateData);
      
      if (templateData?.hasTemplate) {
        const contentType = templateData.contentType;
        setContentType(contentType);
        console.log('Raw trigger conditions:', templateData.triggerConditions);
        
        // Trigger conditions'ı ayarla
        if (templateData.triggerConditions) {
          const tc = templateData.triggerConditions;
          
          // Edge type kontrolü
          const isEdgeType = tc.operator === 'risingEdge' || tc.operator === 'fallingEdge';
          
          setTagTriggerConditions({
            triggerType: isEdgeType ? 'edge' : tc.type || 'change',
            operator: isEdgeType ? 'equals' : tc.operator || 'equals',
            thresholdValue: tc.thresholdValue?.toString() || '',
            risingEdge: tc.operator === 'risingEdge'
          });
        } else {
          // Default values
          setTagTriggerConditions({
            triggerType: 'change',
            operator: 'equals',
            thresholdValue: '',
            risingEdge: true
          });
        }
        
        const content = contentType === 'workflow' 
          ? workflows.find(w => w.name === templateData.taskName)
          : codes.find(c => c.name === templateData.taskName);
          
        if (content) {
          setSelectedContent(content);
          const localVars = extractLocalVariables(content);
          
          const updatedMappings = localVars.map(v => {
            const existingMapping = templateData.mappings?.find(m => m.localVariableName === v.name);
            return {
              localVar: v.name,
              description: existingMapping?.description || v.description || '',
              tagId: tagId
            };
          });
          
          setLocalVarMappings(updatedMappings);
          setSelectedVariables(templateData.mappings?.map(m => m.localVariableName) || []);
        }
      } else {
        setContentType('workflow');
      }
    } catch (error) {
      console.error('Error loading template data:', error);
      toast.error('Error occurred while loading template data');
    } finally {
      setLoading(false);
    }
  };

  // Load template data when dialog opens
  useEffect(() => {
    if (mappingDialogOpen && selectedTag && activeTab === 0) {
      loadTemplateData(selectedTag.tagID);
    }
  }, [mappingDialogOpen, selectedTag]);

  // Load tag mappings
  useEffect(() => {
    if (workflows.length > 0 || codes.length > 0) {
      loadTagMappings();
    }
  }, [workflows.length, codes.length]);

  // Clear modal search when dialog closes or content type changes
  useEffect(() => {
    if (!mappingDialogOpen) {
      setModalSearchTerm('');
    }
  }, [mappingDialogOpen]);

  useEffect(() => {
    setModalSearchTerm('');
  }, [contentType]);

  const loadTagMappings = async () => {
    try {
      setLoading(true);
      const results = await api.getAllTagTemplateStatus();
      console.log('loadTagMappings results =', results);
      
      setTags(prevTags => 
        prevTags.map(tag => {
          const mapping = results.find(m => m.TagId === tag.tagID);
          
          if (!mapping || !mapping.HasTemplate) {
            return {
              ...tag,
              hasTemplate: false,
              templateName: null,
              contentType: null
            };
          }
  
          let templateName = mapping.TaskName;
          let contentType = mapping.ContentType;
          
          if (contentType === 'workflow') {
            const workflow = workflows.find(w => w.id === parseInt(mapping.TaskName));
            if (workflow) {
              templateName = workflow.name;
            }
          } else if (contentType === 'code') {
            const code = codes.find(c => c.id === parseInt(mapping.TaskName));
            if (code) {
              templateName = code.name;
            }
          }
  
          return {
            ...tag,
            hasTemplate: true,
            templateName: templateName,
            contentType: contentType
          };
        })
      );
    } catch (error) {
      console.error('Error loading tag mappings:', error);
      toast.error('Error occurred while loading tag mapping information');
    } finally {
      setLoading(false);
    }
  };

  const refreshTaskTagCounts = async () => {
    try {
      const templateStatusData = await api.getAllTagTemplateStatus();
      
      setWorkflows(prevWorkflows => prevWorkflows.map(w => ({
        ...w,
        tagCount: templateStatusData.filter(ts => 
          ts.HasTemplate === 1 && 
          ts.ContentType === 'workflow' && 
          ts.TaskName === w.name
        ).length
      })));

      setCodes(prevCodes => prevCodes.map(c => ({
        ...c,
        tagCount: templateStatusData.filter(ts => 
          ts.HasTemplate === 1 && 
          ts.ContentType === 'code' && 
          ts.TaskName === c.name
        ).length
      })));
    } catch (error) {
      console.error('Error refreshing task tag counts:', error);
      toast.error('Error occurred while updating tag counts');
    }
  };
  const extractLocalVariables = (content) => {
    if (!content) return [];
    
    try {
      if (content.json) {
        try {
          const firstParse = typeof content.json === 'string' ? JSON.parse(content.json) : content.json;
          
          if (content.contentType === 'workflow') {
            const variables = [];
            
            if (Array.isArray(firstParse.localVars)) {
              const localVars = firstParse.localVars.map(variable => ({
                name: variable.name,
                description: variable.description || '',
                type: variable.type || 'string'
              }));
              variables.push(...localVars);
            }
            
            if (Array.isArray(firstParse.nodes)) {
              const nodeVars = firstParse.nodes
                .filter(node => node.data && node.data.outputVariable)
                .map(node => ({
                  name: node.data.outputVariable,
                  description: `Output from ${node.data.Title || node.type}`,
                  type: 'string'
                }));
              variables.push(...nodeVars);
              
              const elementDetailVars = firstParse.nodes
                .filter(node => node.data && node.data.ElementDetail)
                .flatMap(node => 
                  node.data.ElementDetail
                    .filter(detail => detail.elementDetailVariableType === 'V')
                    .map(detail => ({
                      name: `var_${detail.elementDetailVariableId}`,
                      description: `Variable from ${node.data.Title || node.type}`,
                      type: 'string'
                    }))
                );
              variables.push(...elementDetailVars);
            }
            
            const uniqueVars = Array.from(new Map(
              variables.map(v => [v.name, v])
            ).values());
            
            return uniqueVars;
          }
        } catch (error) {
          console.error('JSON parsing error:', error);
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error in extractLocalVariables:', error);
      return [];
    }
  };

  const loadTaskMappings = async (taskName) => {
    try {
        if (!taskName) {
            console.error('Task name is undefined');
            return;
        }
    
        console.log('Loading mappings for task:', taskName);
        const mappings = await api.getTaskTemplateMappings(taskName);
        console.log('Raw mappings from API:', mappings);
    
        if (!mappings) {
            console.log('No mappings found');
            setVariableMappings([]);
            setSelectedLocalVariable(null);
            setSelectedTaskTags([]);
            return;
        }

        // Backend'den gelen veriyi dönüştür
        const formattedMappings = Object.entries(mappings).map(([variableName, tagData]) => {
            console.log(`Processing variable ${variableName}, tagData:`, tagData);
            
            let tagsArray = Array.isArray(tagData) ? tagData : [tagData];
            tagsArray = tagsArray.filter(tag => tag != null);
            
            // Her tag için trigger conditions'ı parse et
            const tagsWithParsedTriggers = tagsArray.map(tag => {
                let parsedTriggerConditions;
                
                try {
                    // TriggerConditionsJson string ise parse et
                    if (typeof tag.TriggerConditionsJson === 'string') {
                        parsedTriggerConditions = JSON.parse(tag.TriggerConditionsJson);
                        console.log('Parsed trigger conditions:', parsedTriggerConditions);
                    } 
                    // Direkt obje olarak geldiyse
                    else if (tag.triggerConditions) {
                        parsedTriggerConditions = tag.triggerConditions;
                    }
                    // TriggerConditions direkt olarak geldiyse
                    else if (tag.TriggerConditions) {
                        parsedTriggerConditions = tag.TriggerConditions;
                    }
                    
                    // Parse edilmiş trigger conditions'ı normalize et
                    if (parsedTriggerConditions) {
                        // Edge durumu için özel kontrol
                        if (parsedTriggerConditions.Type === 'edge' || 
                            parsedTriggerConditions.Operator === 'risingEdge' || 
                            parsedTriggerConditions.Operator === 'fallingEdge') {
                            
                            parsedTriggerConditions = {
                                Type: 'edge',
                                Operator: parsedTriggerConditions.RisingEdge ? 'risingEdge' : 'fallingEdge',
                                ThresholdValue: null,
                                RisingEdge: parsedTriggerConditions.RisingEdge || 
                                           parsedTriggerConditions.Operator === 'risingEdge'
                            };
                        }
                    }

                    console.log('Final trigger conditions for tag:', parsedTriggerConditions);
                } catch (e) {
                    console.error('Error parsing trigger conditions:', e);
                    parsedTriggerConditions = null;
                }

                return {
                    ...tag,
                    triggerConditions: parsedTriggerConditions || {
                        Type: 'change',
                        Operator: 'equals',
                        ThresholdValue: '',
                        RisingEdge: false
                    }
                };
            });

            return {
                variableName,
                tags: tagsWithParsedTriggers
            };
        });

        console.log('Formatted mappings:', formattedMappings);
        
        setVariableMappings(formattedMappings);
        setSelectedLocalVariable(null);
        setSelectedTaskTags([]);
    
    } catch (error) {
        console.error('Error loading task mappings:', error);
        toast.error('Error occurred while loading mappings');
    }
  };

  const handleContentSelect = async (content) => {
    try {
      console.log('Selected content:', content);
      
      // Eğer content bir string ise (Select'ten geliyorsa)
      if (typeof content === 'string') {
        let selectedContent;
        if (contentType === 'workflow') {
          selectedContent = workflows.find(w => w.name === content);
        } else {
          selectedContent = codes.find(c => c.name === content);
        }
        
        if (!selectedContent) {
          throw new Error('Content not found');
        }
        
        setSelectedContent(selectedContent);
      } else {
        // Eğer content bir obje ise (direkt task seçiminden geliyorsa)
        setSelectedContent(content);
        setContentType(content.contentType);
      }
      
      // Seçilen content'i kullanarak işlemlere devam et
      const currentContent = typeof content === 'string' 
        ? (contentType === 'workflow' 
            ? workflows.find(w => w.name === content)
            : codes.find(c => c.name === content))
        : content;
  
      if (activeTab === 1) {
        await loadTaskMappings(currentContent.name);
      }
      
      const localVars = extractLocalVariables(currentContent);
      
      if (localVars.length > 0) {
        const updatedMappings = localVars.map(v => {
          const existingMapping = localVarMappings.find(m => m.localVar === v.name);
          return {
            localVar: v.name,
            description: existingMapping?.description || v.description || '',
            tagId: existingMapping?.tagId || selectedTag?.tagID || null,
            tagName: existingMapping?.tagName || selectedTag?.tagName || null
          };
        });
        
        setLocalVarMappings(updatedMappings);
        
        if (selectedVariables.length === 0) {
          setSelectedVariables(localVars.map(v => v.name));
        }
      } else {
        setLocalVarMappings([]);
        setSelectedVariables([]);
      }
    } catch (error) {
      console.error('Error in handleContentSelect:', error);
      toast.error('Error occurred while loading content');
    }
  };

  const handleSaveTemplate = async () => {
    try {
      if (!selectedContent || !selectedTag) {
        toast.error('Content or tag not selected');
        return;
      }
  
      const mappings = selectedVariables.map(varName => {
        const mapping = localVarMappings.find(m => m.localVar === varName);
        return {
            companyId: 1,
            tagId: selectedTag.tagID,
            localVariableName: varName,
            description: mapping?.description || '',
            contentType: contentType,
            triggerConditions: {
                type: tagTriggerConditions.triggerType,
                operator: tagTriggerConditions.triggerType === 'edge' 
                    ? (tagTriggerConditions.risingEdge ? 'risingEdge' : 'fallingEdge')
                    : tagTriggerConditions.operator,
                thresholdValue: tagTriggerConditions.triggerType === 'threshold' 
                    ? parseFloat(tagTriggerConditions.thresholdValue) 
                    : null,
                risingEdge: tagTriggerConditions.triggerType === 'edge' 
                    ? tagTriggerConditions.risingEdge 
                    : null
            }
          };
      });
  
      // Tag template request objesi
      const templateRequest = {
        taskName: selectedContent.name,
        contentType: contentType,
        tagId: selectedTag.tagID,
        mappings: mappings,
        triggerConditions: {
          type: tagTriggerConditions.triggerType,
          operator: tagTriggerConditions.triggerType === 'edge' 
            ? (tagTriggerConditions.risingEdge ? 'risingEdge' : 'fallingEdge')
            : tagTriggerConditions.operator,
          thresholdValue: tagTriggerConditions.triggerType === 'threshold' 
            ? parseFloat(tagTriggerConditions.thresholdValue) 
            : null
        }
      };
  
      // Mevcut saveTaskTemplateMappings fonksiyonunu kullan
      await api.saveTaskTemplateMappings(selectedContent.name, mappings, templateRequest.triggerConditions);
      await refreshTaskTagCounts();
      await loadTagMappings();
      
      setMappingDialogOpen(false);
      toast.success('Template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error occurred while saving template');
    }
  };

  const handleSaveTaskMapping = async () => {
    try {
        if (!selectedContent || !selectedLocalVariable) {
            toast.error('Task or variable not selected');
            return;
        }

        // Debug için seçili değerleri kontrol et
        console.log('Selected Content:', selectedContent);
        console.log('Selected Local Variable:', selectedLocalVariable);
        console.log('Selected Task Tags:', selectedTaskTags);
        console.log('Task Trigger Conditions:', taskTriggerConditions);

        // Mapping'leri backend modeline uygun şekilde oluştur
        const newMappings = selectedTaskTags.map(tag => {
            // Trigger conditions objesini backend modeline uygun şekilde oluştur
            const triggerConditions = {
                Type: taskTriggerConditions.triggerType,
                Operator: taskTriggerConditions.triggerType === 'edge' 
                    ? (taskTriggerConditions.risingEdge ? 'risingEdge' : 'fallingEdge')
                    : taskTriggerConditions.operator,
                ThresholdValue: taskTriggerConditions.triggerType === 'threshold' 
                    ? parseFloat(taskTriggerConditions.thresholdValue) 
                    : null,
                RisingEdge: taskTriggerConditions.triggerType === 'edge' 
                    ? taskTriggerConditions.risingEdge 
                    : null
            };

            // Mapping objesi
            const mapping = {
                Id: 0,
                CompanyId: 1,
                TagId: parseInt(tag.tagID),
                TaskName: selectedContent.name,
                ContentType: selectedContent.contentType || 'workflow',
                LocalVariableName: selectedLocalVariable,
                Description: '',
                TriggerConditionsJson: JSON.stringify(triggerConditions)
            };

            // Debug için oluşturulan mapping'i kontrol et
            console.log('Created mapping:', mapping);

            return mapping;
        });

        // Debug için tüm mapping'leri kontrol et
        console.log('All mappings to save:', newMappings);

        // Mapping'lerde null değer kontrolü yap
        const hasNullValues = newMappings.some(mapping => 
            !mapping.TaskName || 
            !mapping.ContentType || 
            !mapping.LocalVariableName || 
            !mapping.TagId
        );

        if (hasNullValues) {
            console.error('Required fields are missing:', newMappings);
            throw new Error('Required mapping fields are missing');
        }

        if (newMappings.length > 0) {
            // API çağrısını yap
            const saveResult = await api.saveTaskTemplateMappings(selectedContent.name, newMappings);
            console.log('Save result:', saveResult);

            // UI güncellemesi
            const updatedMappings = variableMappings.map(mapping => {
                if (mapping.variableName === selectedLocalVariable) {
                    return {
                        ...mapping,
                        tags: selectedTaskTags,
                        triggerConditions: taskTriggerConditions
                    };
                }
                return mapping;
            });

            setVariableMappings(updatedMappings);
            await refreshTaskTagCounts();
            
            toast.success('Tag mappings updated successfully');
            setMappingDialogOpen(false);
        }
        
    } catch (error) {
        console.error('Error updating task mappings:', error);
        console.error('Error details:', error.response?.data);
        toast.error('Error occurred while updating tag mappings. Please check all fields.');
    }
  };
  // Template info rendering
  const renderTemplateInfo = (tag) => {
    if (!tag.hasTemplate) return 'Undefined';
  
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {tag.contentType === 'workflow' ? (
          <Workflow size={16} />
        ) : (
          <Code size={16} />
        )}
        {tag.templateName}
      </Box>
    );
  };

  const TagSelectionList = ({ tags, selectedTags, onChange, disabled }) => {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Matching Tags
        </Typography>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTags.length === tags.length}
                    indeterminate={selectedTags.length > 0 && selectedTags.length < tags.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange(tags);
                      } else {
                        onChange([]);
                      }
                    }}
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>Tag Name</TableCell>
                <TableCell>ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((tag) => (
                <TableRow key={tag.tagID}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTags.some(t => t.tagID === tag.tagID)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange([...selectedTags, tag]);
                        } else {
                          onChange(selectedTags.filter(t => t.tagID !== tag.tagID));
                        }
                      }}
                      disabled={disabled}
                    />
                  </TableCell>
                  <TableCell>{tag.tagName}</TableCell>
                  <TableCell>{tag.tagID}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Trigger conditions için render fonksiyonu
  const renderTriggerConditions = () => (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Trigger Conditions
      </Typography>
      
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Trigger Type</InputLabel>
        <Select
          value={tagTriggerConditions.triggerType}
          label="Trigger Type"
          onChange={(e) => setTagTriggerConditions(prev => ({
            ...prev,
            triggerType: e.target.value
          }))}
        >
          {triggerTypes.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {tagTriggerConditions.triggerType === 'threshold' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Comparison Operator</InputLabel>
            <Select
              value={tagTriggerConditions.operator}
              label="Comparison Operator"
              onChange={(e) => setTagTriggerConditions(prev => ({
                ...prev,
                operator: e.target.value
              }))}
            >
              {triggerOperators.map(op => (
                <MenuItem key={op.value} value={op.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontFamily: 'monospace' }}>{op.icon}</Typography>
                    {op.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Threshold Value"
            type="number"
            value={tagTriggerConditions.thresholdValue}
            onChange={(e) => setTagTriggerConditions(prev => ({
              ...prev,
              thresholdValue: e.target.value
            }))}
          />
        </>
      )}

      {tagTriggerConditions.triggerType === 'edge' && (
        <FormControl component="fieldset">
          <RadioGroup
            value={tagTriggerConditions.risingEdge ? 'rising' : 'falling'}
            onChange={(e) => setTagTriggerConditions(prev => ({
              ...prev,
              risingEdge: e.target.value === 'rising'
            }))}
          >
            <FormControlLabel 
              value="rising" 
              control={<Radio />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowUpRight size={16} />
                  <span>Rising Edge (0 → 1)</span>
                </Box>
              }
            />
            <FormControlLabel 
              value="falling" 
              control={<Radio />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowDownRight size={16} />
                  <span>Falling Edge (1 → 0)</span>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      )}
    </Box>
  );

const handleLocalVariableChange = async (varName) => {
  console.log('Selected variable:', varName);
  setSelectedLocalVariable(varName);
  
  try {
      // Seçilen task için tüm mappingleri al
      const allMappings = await api.getTaskTemplateMappings(selectedContent.name);
      console.log('All mappings from API:', allMappings);
      
      // Seçilen değişken için mappingleri filtrele
      const variableMappings = allMappings.filter(mapping => 
          mapping.localVariableName === varName
      );
      
      console.log('Filtered mappings for variable:', variableMappings);
      
      if (variableMappings && variableMappings.length > 0) {
          // İlk mapping'den trigger conditions'ı al
          const firstMapping = variableMappings[0];
          const tc = firstMapping.triggerConditions;
          console.log('Trigger conditions from first mapping:', tc);
          
          // Trigger conditions'ı normalize et
          let normalizedConditions = {
              triggerType: tc.type || 'change',
              operator: tc.operator || 'equals',
              thresholdValue: tc.thresholdValue?.toString() || '',
              risingEdge: false
          };
          
          // Edge type için özel kontrol
          if (tc.type === 'edge' || tc.operator === 'risingEdge' || tc.operator === 'fallingEdge') {
              normalizedConditions = {
                  triggerType: 'edge',
                  operator: 'equals',
                  thresholdValue: '',
                  risingEdge: tc.operator === 'risingEdge' || tc.risingEdge === true
              };
          }
          // Threshold type için özel kontrol
          else if (tc.type === 'threshold') {
              normalizedConditions = {
                  triggerType: 'threshold',
                  operator: tc.operator || 'equals',
                  thresholdValue: tc.thresholdValue?.toString() || '',
                  risingEdge: false
              };
          }
          
          console.log('Normalized conditions:', normalizedConditions);
          setTaskTriggerConditions(normalizedConditions);
          
          // Seçili tag'leri ayarla
          const selectedMappingTags = await Promise.all(
              variableMappings.map(async (mapping) => {
                  const matchingTag = tags.find(t => t.tagID === mapping.tagId);
                  if (matchingTag) {
                      return {
                          ...matchingTag,
                          triggerConditions: mapping.triggerConditions
                      };
                  }
                  return null;
              })
          );
          
          // Null değerleri filtrele
          const validTags = selectedMappingTags.filter(tag => tag !== null);
          console.log('Selected tags:', validTags);
          setSelectedTaskTags(validTags);
          
      } else {
          // Eşleşme yoksa varsayılan değerleri ayarla
          setTaskTriggerConditions({
              triggerType: 'change',
              operator: 'equals',
              thresholdValue: '',
              risingEdge: false
          });
          setSelectedTaskTags([]);
      }
      
    } catch (error) {
      console.error('Error in handleLocalVariableChange:', error);
      toast.error('Error occurred while loading tag mappings');
    }
  };


  const handleTriggerConditionsChange = (newConditions) => {
    console.log('New trigger conditions:', newConditions);
    setTaskTriggerConditions(newConditions);
    
    if (!selectedLocalVariable) return;
  
    // Tüm mapping'leri kontrol et
    const matchingTags = [];
    
    // Tag'leri döngüyle kontrol et ve eşleşenleri bul
    tags.forEach(tag => {
      const existingMapping = variableMappings.find(mapping => 
        mapping.tags?.some(t => 
          t.tagId === tag.tagID && 
          t.localVariableName === selectedLocalVariable
        )
      );
      
      if (existingMapping) {
        // Tag'in trigger conditions kontrol et
        const tagMapping = existingMapping.tags?.find(t => t.tagId === tag.tagID);
        if (tagMapping?.triggerConditions) {
          const tc = tagMapping.triggerConditions;
          
          // Type kontrolü - null/undefined değilse toLowerCase() uygula
          const currentType = tc.Type || tc.type || '';
          const newType = newConditions.triggerType || '';
          const typeMatches = currentType.toLowerCase() === newType.toLowerCase();
          
          // Edge type için özel kontrol
          if (newConditions.triggerType === 'edge') {
            const edgeMatches = 
              (newConditions.risingEdge && (tc.Operator === 'risingEdge' || tc.operator === 'risingEdge')) ||
              (!newConditions.risingEdge && (tc.Operator === 'fallingEdge' || tc.operator === 'fallingEdge'));
              
            if (typeMatches && edgeMatches) {
              matchingTags.push(tag);
            }
          }
          // Threshold type için özel kontrol
          else if (newConditions.triggerType === 'threshold') {
            const currentOperator = tc.Operator || tc.operator || '';
            const newOperator = newConditions.operator || '';
            const operatorMatches = currentOperator.toLowerCase() === newOperator.toLowerCase();
            
            const currentValue = tc.ThresholdValue?.toString() || tc.thresholdValue?.toString() || '';
            const newValue = newConditions.thresholdValue?.toString() || '';
            const valueMatches = currentValue === newValue;
            
            if (typeMatches && operatorMatches && valueMatches) {
              matchingTags.push(tag);
            }
          }
          // Change type için basit kontrol
          else if (typeMatches) {
            matchingTags.push(tag);
          }
        }
      }
    });
    
    console.log('Matching tags:', matchingTags);
    setSelectedTaskTags(matchingTags);
  };

  const renderTaskVariableMappingContent = () => {
    if (!selectedContent) {
      return (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          color: 'text.secondary',
          bgcolor: 'grey.50',
          borderRadius: 1
        }}>
          <AlertCircle size={24} className="mx-auto mb-2" />
          <Typography>Please select a task.</Typography>
        </Box>
      );
    }
  
    // Task'ın local değişkenlerini çıkar
    const localVars = extractLocalVariables(selectedContent);
    console.log('Local variables:', localVars);
    
    // Eğer hiç local değişken yoksa uyarı göster
    if (localVars.length === 0) {
      return (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          color: 'text.secondary',
          bgcolor: 'grey.50',
          borderRadius: 1
        }}>
          <Variable size={24} className="mx-auto mb-2" />
          <Typography>No local variables defined for this task.</Typography>
        </Box>
      );
    }
  
    return (
      <Box sx={{ pt: 2 }}>
        {/* Local değişken seçimi */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Local Variable</InputLabel>
          <Select
            value={selectedLocalVariable || ''}
            label="Local Variable"
            onChange={(e) => handleLocalVariableChange(e.target.value)}
          >
            {localVars.map(variable => (
              <MenuItem key={variable.name} value={variable.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Variable size={16} />
                  <span>{variable.name}</span>
                  {variable.description && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      - {variable.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
  
        {selectedLocalVariable && (
          <>
          <TriggerConditionsForm
            initialConditions={taskTriggerConditions}
            onApply={handleTriggerConditionsChange}
            disabled={!selectedLocalVariable}
          />
          <TagSelectionList
            tags={tags}
            selectedTags={selectedTaskTags}
            onChange={setSelectedTaskTags}
            disabled={!selectedLocalVariable}
          />
        </>
        )}
      </Box>
    );
  };


useEffect(() => {
  if (mappingDialogOpen && selectedContent && activeTab === 1) {
      console.log('Loading mappings for content:', selectedContent); // Debug için
      loadTaskMappings(selectedContent.name);
  }
}, [mappingDialogOpen, selectedContent, activeTab]);

// Ana return bloğu
return (
  <Box sx={{ p: 3, backgroundColor: '#f8f4ff', minHeight: '100vh' }}>
    <Card sx={{ mb: 3 }}>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Tag-Based Template" />
        <Tab label="Task-Based Tag Mapping" />
      </Tabs>

      {activeTab === 0 ? (
        // Tag -> Task View
        <Box>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <Typography variant="h6">Tag List</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search size={18} className="text-gray-400 mr-2" />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Template Status</InputLabel>
                <Select
                  value={tagTemplateFilter}
                  onChange={(e) => setTagTemplateFilter(e.target.value)}
                  label="Template Status"
                >
                  <MenuItem value="all">All Tags</MenuItem>
                  <MenuItem value="hasTemplate">Has Template</MenuItem>
                  <MenuItem value="noTemplate">No Template</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tag Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tags
                  .filter(tag => {
                    // Search filter
                    const matchesSearch = tag.tagName && tag.tagName.toLowerCase().includes(searchTerm.toLowerCase());
                    // Template status filter
                    let matchesTemplate = true;
                    if (tagTemplateFilter === 'hasTemplate') {
                      matchesTemplate = tag.hasTemplate === true;
                    } else if (tagTemplateFilter === 'noTemplate') {
                      matchesTemplate = !tag.hasTemplate;
                    }
                    return matchesSearch && matchesTemplate;
                  })
                  .map(tag => (
                    <TableRow key={tag.tagID}>
                      <TableCell>{tag.tagName}</TableCell>
                      <TableCell>{tag.tagID}</TableCell>
                      <TableCell>
                        {renderTemplateInfo(tag)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedTag(tag);
                            setMappingDialogOpen(true);
                          }}
                        >
                          Template {tag.hasTemplate ? 'Edit' : 'Create'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        // Task -> Tag View
        <Box>
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <Typography variant="h6">Task List</Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tasks..."
                value={taskSearchTerm}
                onChange={(e) => setTaskSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search size={18} className="text-gray-400 mr-2" />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Task Type</InputLabel>
                <Select
                  value={taskTypeFilter}
                  onChange={(e) => setTaskTypeFilter(e.target.value)}
                  label="Task Type"
                >
                  <MenuItem value="all">All Tasks</MenuItem>
                  <MenuItem value="workflow">Workflow</MenuItem>
                  <MenuItem value="code">Code</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Task Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Connected Tag Count</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map(task => (
                  <TableRow key={task.id}>
                    <TableCell>{task.name}</TableCell>
                    <TableCell>
                      {task.contentType === 'workflow' ? 'Workflow' : 'Code'}
                    </TableCell>
                    <TableCell>
                      {task.tagCount || 0}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          console.log('Selected task:', task); // Debug için
                          setSelectedContent(task);
                          setMappingDialogOpen(true);
                        }}
                      >
                        Map Tags
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Card>

    {/* Mapping Dialog */}
    <Dialog
      open={mappingDialogOpen}
      onClose={() => setMappingDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{
        bgcolor: '#edbc45',
        color: '#000',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography sx={{ fontWeight: 600 }}>
          {activeTab === 0 ?
            `Template Settings: ${selectedTag?.tagName}` :
            `Tag Mapping: ${selectedContent?.name}`}
        </Typography>
        <IconButton
          onClick={() => setMappingDialogOpen(false)}
          sx={{ color: '#000' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {activeTab === 0 ? (
          // Tag -> Task Template içeriği
          <Box sx={{ pt: 2 }}>
            {/* Content Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={contentType}
                  label="Content Type"
                  onChange={(e) => {
                    setContentType(e.target.value);
                    setSelectedContent(null);
                  }}
                >
                  <MenuItem value="workflow">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Workflow size={16} />
                      <span>Workflow</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="code">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Code size={16} />
                      <span>Code</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Search Field */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={`Search ${contentType === 'workflow' ? 'workflows' : 'codes'}...`}
                value={modalSearchTerm}
                onChange={(e) => setModalSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search size={18} style={{ marginRight: 8, color: '#999' }} />
                }}
              />
            </Box>

            {/* Workflow/Code Seçimi */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>{contentType === 'workflow' ? 'Workflow' : 'Code'} Selection</InputLabel>
                <Select
                  value={selectedContent?.name || ''}
                  label={contentType === 'workflow' ? 'Workflow Selection' : 'Code Selection'}
                  onChange={(e) => handleContentSelect(e.target.value)}
                >
                  {(contentType === 'workflow' ? workflows : codes)
                    .filter(item => item.name.toLowerCase().includes(modalSearchTerm.toLowerCase()))
                    .map(item => (
                      <MenuItem key={item.name} value={item.name}>
                        {item.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            {/* Trigger Conditions */}
            {selectedContent && renderTriggerConditions()}

            {/* Local Variable Mappings */}
            {selectedContent && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedVariables.length === localVarMappings.length}
                          indeterminate={selectedVariables.length > 0 && selectedVariables.length < localVarMappings.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedVariables(localVarMappings.map(m => m.localVar));
                            } else {
                              setSelectedVariables([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Local Variable</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localVarMappings.map((mapping, index) => (
                      <TableRow key={mapping.localVar}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedVariables.includes(mapping.localVar)}
                            onChange={() => {
                              setSelectedVariables(prev => {
                                if (prev.includes(mapping.localVar)) {
                                  return prev.filter(v => v !== mapping.localVar);
                                }
                                return [...prev, mapping.localVar];
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>{mapping.localVar}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={mapping.description}
                            onChange={(e) => {
                              const newMappings = [...localVarMappings];
                              newMappings[index].description = e.target.value;
                              setLocalVarMappings(newMappings);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : (
          // Task -> Tag Mapping içeriği
          <>
            {!selectedContent ? (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                color: 'text.secondary',
                bgcolor: 'grey.50',
                borderRadius: 1
              }}>
                <AlertCircle size={24} className="mx-auto mb-2" />
                <Typography>
                  Please select a task.
                </Typography>
              </Box>
            ) : (
              renderTaskVariableMappingContent()
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setMappingDialogOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={activeTab === 0 ? handleSaveTemplate : handleSaveTaskMapping}
          startIcon={<Save />}
          disabled={activeTab === 0 ? 
            (!selectedContent || !selectedTag) : 
            (!selectedContent || !selectedLocalVariable)
          }
          sx={{ bgcolor: '#edbc45', color: '#000', fontWeight: 600, '&:hover': { bgcolor: '#d4a03a' } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>

    {/* Variable Selector Dialog */}
    <Dialog 
      open={variableSelectorOpen} 
      onClose={() => setVariableSelectorOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{
        bgcolor: '#edbc45',
        color: '#000',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: 600 }}>Tag Seçimi</span>
        <IconButton
          onClick={() => setVariableSelectorOpen(false)}
          sx={{ color: '#000' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <VariableSelector
          vars={[]}
          tags={tags}
          onVariableSelect={(selectedTag) => {
            if (selectedTag.type === 'T') {
              const newMappings = [...localVarMappings];
              const mappingIndex = newMappings.findIndex(m => m.localVar === selectedVarName);
              if (mappingIndex !== -1) {
                newMappings[mappingIndex].tagId = selectedTag.tagID;
                newMappings[mappingIndex].tagName = selectedTag.tagName;
                setLocalVarMappings(newMappings);
              }
            }
            setVariableSelectorOpen(false);
          }}
          showConstants={false}
        />
      </DialogContent>
    </Dialog>
  </Box>
);
};

export default TemplateManager;